import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  Course,
  CourseAttempt,
  CourseContentType,
  CourseStatusAttemptEnum,
  ResourceType,
} from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { AppContext } from '@/common/context/AppContext';
import { CourseLanguageSelectScreen } from '@/components/courses/CourseLanguageSelectScreen';
import {
  useCourseAttempt,
  useCourseResource,
  useScormDispatchUrl,
} from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useContentViewerActiveClass } from '@/hooks/useContentViewerActiveClass';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser, setCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';

import { QuizType } from '@/client/quizzes';
import { ContentFrameLayoutProvider } from '@/components/courses/content-frame.context';
import { CourseFeedbackModule } from '@/components/courses/CourseFeedbackModule';
import { CoursePdfModule } from '@/components/courses/CoursePdfModule';
import { CourseQuizModule } from '@/components/courses/CourseQuizModule';
import { CourseSlidesModule } from '@/components/courses/CourseSlidesModule';
import { CourseVideoModule } from '@/components/courses/CourseVideoModule';
import {
  attemptLog,
  determinePreselectedCourseLanguage,
  getCourseName,
  isRtlLanguage,
  resolveAttemptLanguage,
  toBcp47,
} from '@/utils/helpers';
import { isMobile } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccessibilityIcon } from './AccessibilityIcon';
import { ContentActionHeader } from './ContentActionHeader';
import { ContentRetakeScreen } from './ContentRetakeScreen';
import { CourseScormModule } from './CourseScormModule';
import { decideModuleCompletion } from './module-completion';

const StyledCourseRegion = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const StyledCourseLanguageScope = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const StyledFlexContainer = styled(FlexContainer)`
  word-break: break-word;
  min-height: 100%;

  .p-button:focus:not(:focus-visible),
  .p-button:enabled:focus:not(:focus-visible),
  .p-button.p-button-secondary:enabled:focus:not(:focus-visible) {
    box-shadow: none !important;
  }

  .p-button:focus-visible,
  .p-button:enabled:focus-visible,
  .p-button.p-button-secondary:enabled:focus-visible {
    box-shadow: 0 0 0 2px var(--white-main), 0 0 0 4px var(--purple-text) !important;
  }

  &.stretch {
    height: 100%;
  }

  @media screen and (max-width: 424px) {
    &.stretch {
      height: auto;
    }
  }
`;

const StyledButton = styled(AppButton)`
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;

  &.exit-button {
    position: absolute;
    right: var(--xxsmall-padding);
    top: var(--xsmall-padding);
    z-index: 999;
  }
`;

export const ContentPlayer: React.FC<{ course: Course }> = ({ course }) => {
  const { t } = useTranslation();
  const mobile = isMobile();
  const accessibilityIcon = mobile ? undefined : <AccessibilityIcon />;
  const { triggerIsFullScreen } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { id: courseId, attemptId } = useParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const [status, setStatus] = useState<CourseStatusAttemptEnum>();
  const [score, setScore] = useState<number>();
  const [isContentStarted, setIsContentStarted] = useState<boolean>(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState<boolean>(false);
  const [isBellowThreshold, setIsBellowThreshold] = useState<boolean>(false);

  const [selectedLanguage, setSelectedLanguage] = useState<
    LanguagesEnum | undefined
  >(undefined);
  const modules = useMemo(
    () =>
      [...(course.courseContents ?? [])].sort(
        (a, b) => a.position - b.position,
      ),
    [course.courseContents],
  );
  const [currentModuleIndex, setCurrentModuleIndex] = useState<number>(0);
  const attemptLanguage = resolveAttemptLanguage(course, selectedLanguage);
  const currentModule = modules[currentModuleIndex];
  const currentResourceId =
    currentModule?.type === CourseContentType.RESOURCE
      ? currentModule.resource?.id
      : undefined;
  const { resource: currentResource } = useCourseResource({
    courseId: course?.id,
    resourceId: currentResourceId,
    onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
  });
  const { scormDetails, isLoading: isDispatchUrlLoading } = useScormDispatchUrl(
    {
      courseId: course?.id,
      resourceId:
        currentResource?.type === ResourceType.SCORM
          ? currentResource.id
          : undefined,
      language: attemptLanguage,
      onError: (e) => handleAxiosError(e as Error | AxiosError, toast),
    },
  );

  const isRTL = isRtlLanguage(attemptLanguage);
  const courseName = getCourseName(course, currentUser);

  useEffect(() => {
    if (!triggerIsFullScreen) return;

    if (mobile) {
      triggerIsFullScreen(true);
    }

    return () => {
      triggerIsFullScreen(false);
    };
  }, []);

  useContentViewerActiveClass();

  useEffect(() => {
    const fetchUser = async () => {
      const user = await client.default.getMe(currentUser?.account.id);
      dispatch(setCurrentUser(user));
    };
    fetchUser();
  }, []);

  const {
    courseAttempt: scormCourseAttempt,
    isLoading: isAttemptLoading,
    refetch: refetchAttempt,
  } = useCourseAttempt({
    courseId,
    attemptId,
  });

  useEffect(() => {
    if (!currentUser || !course || isAttemptLoading) {
      return;
    }

    // Set initial preselected language if not available set to English
    setSelectedLanguage(
      determinePreselectedCourseLanguage(currentUser, course),
    );

    // Workaround for wrong attemptId given in reminder emails
    const checkAttemptExistance = async () => {
      if (course?.id && attemptId) {
        const attempt = await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: attemptId,
          language: determinePreselectedCourseLanguage(currentUser, course),
        });

        if (attempt.id !== attemptId) {
          navigate(
            RedirectPaths[RedirectPathsEnum.COURSE_PLAY](course.id, attempt.id),
            { replace: true },
          );
        }
      }
    };

    checkAttemptExistance();
  }, [currentUser, course, scormCourseAttempt]);

  useEffect(() => {
    const handleCourseStart = async () => {
      if (course?.selfHosted && course?.courseLanguages?.length === 1) {
        setIsContentStarted(true);
      }

      if (currentUser?.contentLanguage && !mobile) {
        setIsContentStarted(true);

        await attemptLog(
          course,
          attemptId,
          `Course started - course: ${course.id}, user language: ${currentUser?.contentLanguage}`,
        );
      }
    };

    handleCourseStart();
  }, [course, scormCourseAttempt]);

  useEffect(() => {
    setStatus(scormCourseAttempt?.status);
    setScore(scormCourseAttempt?.score);
  }, [scormCourseAttempt]);

  useEffect(() => {
    setCurrentModuleIndex(0);
  }, [selectedLanguage]);

  useEffect(() => {
    if (attemptId !== scormCourseAttempt?.id) {
      refetchAttempt();
    }
  }, [attemptId]);

  const updateCourseAttemptLanguage = async (language: LanguagesEnum) => {
    try {
      if (attemptId) {
        await client.learnerCourses.updateCourseAttempt({
          courseId: course.id,
          attemptId: attemptId,
          language,
          updateContentLanguage: true,
        });
      }
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  useEffect(() => {
    if (!scormDetails?.url) return;

    void attemptLog(
      course,
      attemptId,
      `Course file fetched - course: ${course.id}, language: ${attemptLanguage}, file: ${scormDetails.url}`,
    );
  }, [scormDetails?.url]);

  const advanceToNextModule = () => {
    setCurrentModuleIndex(currentModuleIndex + 1);
  };

  const handleVideoPlaybackError = (video: HTMLVideoElement) => {
    toast?.error(t('error'), t('course.video.playback.error'));

    void attemptLog(
      course,
      attemptId,
      `Video playback failed - resource: ${currentResource?.id}, language: ${attemptLanguage}, mediaErrorCode: ${video.error?.code}, mediaErrorMessage: ${video.error?.message}, readyState: ${video.readyState}, networkState: ${video.networkState}, videoDimensions: ${video.videoWidth}x${video.videoHeight}`,
    );
  };

  const goToPreviousModule = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
    }
  };

  const goToDashboard = () => {
    navigate(RedirectPaths[RedirectPathsEnum.LEARNER_DASHBOARD_ACTIVE]());
  };

  const persistCompletion = async (completionScore?: number) => {
    if (!attemptId) return;

    await client.learnerCourses.updateCourseAttempt({
      courseId: course.id,
      attemptId,
      language: attemptLanguage,
      payload: {
        status: CourseStatusAttemptEnum.COMPLETED,
        ...(completionScore !== undefined && { score: completionScore }),
      },
    });

    setStatus(CourseStatusAttemptEnum.COMPLETED);
    toast?.success(t('toast.success'), t('content.completed.success'));
  };

  const onQuizFinish = () => {
    if (currentModuleIndex === modules.length - 1) {
      goToDashboard();
    } else {
      advanceToNextModule();
    }
  };

  const handleModuleCompletion = async (
    currentScore?: number,
    isLastModuleCompletable = false,
  ) => {
    if (!course?.id || !attemptId) return;

    const decision = decideModuleCompletion(
      modules,
      currentModuleIndex,
      isLastModuleCompletable,
    );

    try {
      if (decision.persist === 'quiz-score') {
        await persistCompletion(currentScore || score || 0);
      } else if (decision.persist === 'attempt') {
        await persistCompletion();
      }
    } catch (error) {
      handleAxiosError(error as AxiosError, toast);
    }

    if (decision.navigate === 'dashboard') {
      setTimeout(goToDashboard, 500);
    } else if (decision.navigate === 'next') {
      advanceToNextModule();
    }
  };

  const handleBackToCourse = async () => {
    const quizId = currentModule?.quiz?.id;
    if (!quizId) return;

    let lastAttempt: CourseAttempt | null = null;

    try {
      lastAttempt = await client.quizzes.getLastQuizAttempt(quizId);
    } catch (error: any) {
      if (error?.statusCode !== 404) {
        handleAxiosError(error as AxiosError, toast);
      }
    }

    if (
      quizId &&
      lastAttempt &&
      lastAttempt.status !== CourseStatusAttemptEnum.COMPLETED
    ) {
      try {
        await client.quizzes.abandonQuizAttempt(quizId);
      } catch (error) {
        handleAxiosError(error as AxiosError, toast);
      }
    }

    setCurrentModuleIndex(0);
  };

  return (
    <ContentFrameLayoutProvider framedScroll={false}>
      {(!currentUser || isAttemptLoading) && (
        <FlexContainer direction="column" className="h-full">
          <FlexContainer direction="column" className="mt-5 flex-1">
            <ProgressSpinner />
            <h3>{t('content.loading')}</h3>
          </FlexContainer>
        </FlexContainer>
      )}

      {!isAttemptLoading && currentUser && course && (
        <StyledFlexContainer
          translate="no"
          direction="column"
          align="flex-start"
          justify="flex-start"
          className={[
            'notranslate',
            !isContentStarted && course.selfHosted ? 'stretch' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          gap={24}
          height={
            currentModule?.type === CourseContentType.PRESENTATION
              ? '100%'
              : 'initial'
          }
        >
          {!mobile && (
            <ContentActionHeader
              course={course}
              status={status}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={async (language) => {
                setSelectedLanguage(language);
                await updateCourseAttemptLanguage(language);
              }}
              isContentStarted={isContentStarted}
              isDisabled={isSubmittingStatus}
            />
          )}

          {mobile && (
            <StyledButton
              className="exit-button"
              size="xs"
              icon="pi pi-times"
              isDisabled={isSubmittingStatus}
              onClick={goToDashboard}
            />
          )}

          <StyledCourseRegion aria-label={courseName || t('content.course')}>
            {isContentStarted && (
              <StyledCourseLanguageScope
                lang={toBcp47(attemptLanguage, { includeRegion: true })}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                {/* QUIZ */}
                {scormCourseAttempt &&
                  currentModule?.quiz?.type === QuizType.TRADITIONAL && (
                    <CourseQuizModule
                      accessibilityIcon={accessibilityIcon}
                      course={course}
                      status={status}
                      quizId={currentModule?.quiz?.id}
                      attemptLanguage={attemptLanguage}
                      courseAttempt={scormCourseAttempt}
                      hasPreviousModules={currentModuleIndex > 0}
                      onComplete={(currentScore: number) => {
                        setScore(currentScore);
                        handleModuleCompletion(currentScore);
                      }}
                      onFinish={onQuizFinish}
                      handleBackToCourse={handleBackToCourse}
                      rtl={isRTL}
                    />
                  )}

                {/* VIDEO */}
                {currentModule?.type === CourseContentType.RESOURCE &&
                  currentResource?.type === ResourceType.VIDEO && (
                    <CourseVideoModule
                      key={currentModuleIndex}
                      moduleContent={{
                        ...currentModule,
                        resource: currentResource,
                      }}
                      language={attemptLanguage}
                      currentModuleIndex={currentModuleIndex}
                      modules={modules}
                      isContentStarted={isContentStarted}
                      rtl={isRTL}
                      isCompact={false}
                      containerMaxWidth="1000px"
                      onLanguageChange="restart"
                      onPlaybackError={handleVideoPlaybackError}
                      onContinue={advanceToNextModule}
                      onFinish={() => handleModuleCompletion(undefined, true)}
                    />
                  )}

                {/* SLIDE DECK VIEWER */}
                {currentModule?.type === CourseContentType.PRESENTATION && (
                  <CourseSlidesModule
                    key={currentModuleIndex}
                    presentationId={currentModule?.presentation?.id}
                    courseId={course?.id}
                    language={attemptLanguage}
                    currentModuleIndex={currentModuleIndex}
                    modules={modules}
                    isContentStarted={isContentStarted}
                    rtl={isRTL}
                    accessibilityIcon={accessibilityIcon}
                    onContinue={advanceToNextModule}
                    onFinish={() => handleModuleCompletion(undefined, true)}
                    onPreviousModule={goToPreviousModule}
                  />
                )}

                {/* PDF */}
                {currentModule?.type === CourseContentType.RESOURCE &&
                  currentResource?.type === ResourceType.PDF && (
                    <CoursePdfModule
                      key={currentModuleIndex}
                      resource={currentResource}
                      requiresReadConfirmation={
                        !!currentResource.requiresReadConfirmation
                      }
                      language={attemptLanguage}
                      currentModuleIndex={currentModuleIndex}
                      modules={modules}
                      isContentStarted={isContentStarted}
                      rtl={isRTL}
                      accessibilityIcon={accessibilityIcon}
                      onContinue={advanceToNextModule}
                      onFinish={() => handleModuleCompletion(undefined, true)}
                      onPreviousModule={goToPreviousModule}
                    />
                  )}

                {/* FEEDBACK */}
                {currentModule?.quiz?.type === QuizType.FEEDBACK && (
                  <CourseFeedbackModule
                    accessibilityIcon={accessibilityIcon}
                    course={course}
                    quizFeedbackId={currentModule?.quiz?.id}
                    attemptLanguage={attemptLanguage}
                    courseAttempt={scormCourseAttempt}
                    onComplete={(score) => handleModuleCompletion(score)}
                    rtl={isRTL}
                  />
                )}
              </StyledCourseLanguageScope>
            )}

            {/* SCORM */}
            {(isContentStarted || !course.selfHosted) &&
              !isDispatchUrlLoading &&
              !isBellowThreshold &&
              scormDetails?.url &&
              currentResource?.type === ResourceType.SCORM && (
                <CourseScormModule
                  course={course}
                  attempt={scormCourseAttempt as CourseAttempt}
                  currentUser={currentUser}
                  scormDetails={scormDetails}
                  isContentStarted={isContentStarted}
                  isLastModule={currentModuleIndex === modules.length - 1}
                  isCompleted={status === CourseStatusAttemptEnum.COMPLETED}
                  isSubmittingStatus={isSubmittingStatus}
                  setStatus={setStatus}
                  setScore={setScore}
                  setIsSubmittingStatus={setIsSubmittingStatus}
                  setIsBellowThreshold={setIsBellowThreshold}
                  onNextModule={advanceToNextModule}
                />
              )}

            {/* RETAKE SCREEN */}
            {isBellowThreshold &&
              currentResource?.type === ResourceType.SCORM &&
              !!currentUser?.account?.courseMinimumScore &&
              !course.exemptFromMinimumRequirementScore && (
                <ContentRetakeScreen
                  minimumScore={currentUser.account.courseMinimumScore}
                  score={score}
                  isRetakeDisabled={isSubmittingStatus}
                  onRetake={() => setIsBellowThreshold(false)}
                />
              )}

            {/* LANGUAGE SELECT SCREEN */}
            {!isContentStarted &&
              (course.selfHosted || mobile) &&
              (!currentUser?.contentLanguage || mobile) && (
                <CourseLanguageSelectScreen
                  course={course}
                  initialLanguage={selectedLanguage}
                  isLoading={isDispatchUrlLoading}
                  isDisabled={!modules?.length}
                  setIsCourseStarted={async (value: boolean) => {
                    setIsContentStarted(value);
                    if (value) {
                      await attemptLog(
                        course,
                        attemptId,
                        `Course started - course: ${course.id}, selected language: ${selectedLanguage}`,
                      );
                    }
                  }}
                  setSelectedLanguage={async (language) => {
                    setSelectedLanguage(language);
                    await updateCourseAttemptLanguage(language);
                  }}
                />
              )}
          </StyledCourseRegion>
        </StyledFlexContainer>
      )}
    </ContentFrameLayoutProvider>
  );
};
